import React, { useEffect, useState } from 'react';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import bankService, {
  BankItem,
  BankKind,
} from '../../api/services/bank.service';

interface BankFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bank?: BankItem | null;
}

const inputClass =
  'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

const BankFormModal: React.FC<BankFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  bank,
}) => {
  const isEdit = !!bank;

  const [shortCode, setShortCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [kind, setKind] = useState<BankKind>('bank');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [instructions, setInstructions] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [withdrawEnabled, setWithdrawEnabled] = useState(true);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (bank) {
      setShortCode(bank.shortCode);
      setBankName(bank.bankName);
      setKind(bank.kind);
      setAccountNumber(bank.accountNumber);
      setAccountName(bank.accountName);
      setInstructions(bank.instructions || []);
      setIsActive(bank.isActive);
      setWithdrawEnabled(bank.withdrawEnabled ?? true);
    } else {
      setShortCode('');
      setBankName('');
      setKind('bank');
      setAccountNumber('');
      setAccountName('');
      setInstructions([]);
      setIsActive(true);
      setWithdrawEnabled(true);
    }
    setError(null);
  }, [isOpen, bank]);

  const updateInstruction = (i: number, v: string) => {
    setInstructions((prev) => prev.map((s, idx) => (idx === i ? v : s)));
  };

  const addInstruction = () => setInstructions((prev) => [...prev, '']);
  const removeInstruction = (i: number) =>
    setInstructions((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    setError(null);

    if (!shortCode.trim()) return setError('Short code is required');
    if (!bankName.trim()) return setError('Bank name is required');
    if (!accountNumber.trim()) return setError('Account number is required');
    if (!accountName.trim()) return setError('Account name is required');

    setSaving(true);
    try {
      const cleanInstructions = instructions
        .map((s) => s.trim())
        .filter(Boolean);

      if (isEdit && bank) {
        await bankService.update(bank._id, {
          bankName: bankName.trim(),
          kind,
          accountNumber: accountNumber.trim(),
          accountName: accountName.trim(),
          instructions: cleanInstructions,
          isActive,
          withdrawEnabled,
        });
      } else {
        await bankService.create({
          shortCode: shortCode.trim().toLowerCase(),
          bankName: bankName.trim(),
          kind,
          accountNumber: accountNumber.trim(),
          accountName: accountName.trim(),
          instructions: cleanInstructions,
          isActive,
          withdrawEnabled,
        });
      }

      onSuccess();
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || 'Failed to save bank'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Bank' : 'Add Bank'}
      size="md"
    >
      <div className="space-y-4">
        {error && (
          <div className="text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Short Code</label>
            <input
              type="text"
              value={shortCode}
              onChange={(e) => setShortCode(e.target.value)}
              disabled={isEdit}
              placeholder="cbe, boa, telebirr..."
              className={`${inputClass} ${
                isEdit ? 'bg-gray-50 text-gray-500' : ''
              }`}
            />
            {isEdit && (
              <p className="text-[10px] text-gray-500 mt-1">
                Short code cannot be changed after creation.
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>Bank Name</label>
            <input
              type="text"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Commercial Bank of Ethiopia"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Kind</label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as BankKind)}
              className={inputClass}
            >
              <option value="bank">Bank</option>
              <option value="wallet">Wallet</option>
            </select>
            <p className="text-[10px] text-gray-500 mt-1">
              Wallets use Ethiopian phone numbers; banks use account numbers.
            </p>
          </div>

          <div>
            <label className={labelClass}>
              {kind === 'wallet' ? 'Phone Number' : 'Account Number'}
            </label>
            <input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder={kind === 'wallet' ? '09...' : '1000123456789'}
              className={inputClass}
            />
          </div>

          <div className="md:col-span-2">
            <label className={labelClass}>Account Name</label>
            <input
              type="text"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="Full name on the account"
              className={inputClass}
            />
          </div>

          <div className="md:col-span-2 flex flex-wrap items-center gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 accent-primary-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={withdrawEnabled}
                onChange={(e) => setWithdrawEnabled(e.target.checked)}
                className="w-4 h-4 accent-primary-500"
              />
              <span className="text-sm text-gray-700">
                Withdrawal Enabled
              </span>
            </label>
          </div>
        </div>

        {/* Instructions */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelClass}>Instructions (optional)</label>
            <button
              onClick={addInstruction}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Add Step
            </button>
          </div>

          {instructions.length === 0 && (
            <p className="text-xs text-gray-500">
              No instructions yet. These will be shown to users during deposit.
            </p>
          )}

          <div className="space-y-2">
            {instructions.map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-400 w-5 text-center">
                  {i + 1}
                </span>
                <input
                  type="text"
                  value={step}
                  onChange={(e) => updateInstruction(i, e.target.value)}
                  placeholder={`Step ${i + 1}`}
                  className={`${inputClass} flex-1`}
                />
                <button
                  onClick={() => removeInstruction(i)}
                  className="p-2 rounded-lg hover:bg-red-50 text-red-500 flex-shrink-0"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Bank'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BankFormModal;